/**
 * PipelineColumn - Single Kanban column in the pipeline board.
 * Acts as a drop target via @dnd-kit/core useDroppable.
 * Renders a list of PipelineCard components.
 */

import { useDroppable } from '@dnd-kit/core';
import type { PipelineJob, PipelineStatus } from '@/types/pipeline';
import { PipelineCard } from './PipelineCard';

interface PipelineColumnProps {
  id: PipelineStatus;
  title: string;
  color: string;
  jobs: PipelineJob[];
  onMoveToNext: (jobId: string) => void;
  onDelete: (jobId: string) => void;
}

const EMPTY_MESSAGES: Record<PipelineStatus, string> = {
  saved: 'Save jobs you find interesting',
  applied: 'Jobs you have applied to',
  interview: 'Jobs with scheduled interviews',
  offer: 'Congratulations on your offers!',
  rejected: 'Track rejections for insights',
};

export function PipelineColumn({
  id,
  title,
  color,
  jobs,
  onMoveToNext,
  onDelete,
}: PipelineColumnProps) {
  const { isOver, setNodeRef } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={`pipeline-column ${isOver ? 'pipeline-column--drag-over' : ''}`}
    >
      {/* Column header */}
      <div className="pipeline-column__header">
        <span className="pipeline-column__dot" style={{ backgroundColor: color }} />
        <span className="pipeline-column__title">{title}</span>
        <span className="pipeline-column__count">{jobs.length}</span>
      </div>

      {/* Card list or empty state */}
      <div className="pipeline-column__list">
        {jobs.length === 0 ? (
          <div className="pipeline-column__empty">{EMPTY_MESSAGES[id]}</div>
        ) : (
          jobs.map((job) => (
            <PipelineCard
              key={job.id}
              job={job}
              onMoveToNext={onMoveToNext}
              onDelete={onDelete}
            />
          ))
        )}
      </div>
    </div>
  );
}
